import { redactSensitiveUrlParams } from '@/utils/redact-tracing-urls';
import { setCustomUrl, setReferrerUrl, trackPageView } from '@snowplow/browser-tracker';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { isSnowplowInitialized } from './index';

// Last URL we emitted a page view for (path + query, hash ignored). A render or
// redirect loop re-fires `routeChangeComplete` for an unchanged URL many times
// per second; without deduping, each iteration emits a page view. This guard
// caps any such loop to a single event regardless of the upstream cause.
// See the reviewMember redirect-loop incident (~8.7M phantom page views).
let lastTrackedUrl: string | null = null;

// Redacted URL of the last page synced; it is the referrer of the next one.
let lastSyncedUrl: string | null = null;

/**
 * Snowplow stamps `document.location` and `document.referrer` on every event,
 * and only Datadog RUM runs the query-string redactor. Pin both to their
 * redacted forms on mount and on each route change, so auth callbacks and
 * Auth0's hosted onboarding redirect (`?state=…`) never ship a credential.
 *
 * `setReferrerUrl` overrides Snowplow's own SPA referrer chain for good, so
 * this feeds it the chain: the external referrer on the first page of the
 * document, then the previous synced page URL, already redacted. Snowplow's own
 * chain is derived from the raw location and would ship the previous page's
 * `?state=` as `refr`.
 */
export function syncSnowplowPageUrl(): void {
  if (!isSnowplowInitialized() || typeof window === 'undefined') return;

  const url = redactSensitiveUrlParams(window.location.href);
  const referrer =
    lastSyncedUrl === null
      ? document.referrer
        ? redactSensitiveUrlParams(document.referrer)
        : null
      : lastSyncedUrl;

  setCustomUrl(url);
  // A repeated routeChangeComplete for an unchanged URL must not make a page
  // its own referrer.
  if (referrer && referrer !== url) setReferrerUrl(referrer);
  lastSyncedUrl = url;
}

function trackPageViewDeduped(url: string | undefined): void {
  if (!isSnowplowInitialized()) return;
  syncSnowplowPageUrl();

  // Dedupe on path + query, ignoring the hash fragment.
  const key = (url ?? '').split('#')[0] ?? '';
  if (key === lastTrackedUrl) return;

  lastTrackedUrl = key;
  trackPageView();
}

/** Test-only: clears the dedupe latch between cases. */
export function __resetPageViewDedupeForTests(): void {
  lastTrackedUrl = null;
  lastSyncedUrl = null;
}

/**
 * Tracks Snowplow page view events on mount and on Next.js route changes.
 * Single source of truth for all page view tracking.
 *
 * Consecutive page views for the same URL are deduped so a render/redirect
 * loop cannot flood the pipeline.
 */
export function useSnowplowPageView() {
  const router = useRouter();

  useEffect(() => {
    trackPageViewDeduped(router.asPath);

    const handleRouteChange = (url: string) => trackPageViewDeduped(url);

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // router.events is a stable singleton; asPath is read once for the initial view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);
}
